import type { Variants } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { m as motion } from 'framer-motion'

// ---------------------------------------------------------------------------
// Editorial detail-pane primitives (opt-in per surface).
//
// The Focus-page editorial grammar applied to a settings detail pane:
// structure comes from typography and whitespace, not nested card chrome.
// Section headers carry an auto-incrementing ghost numeral (CSS counters) and
// a hairline rule that fades right; statuses are a colored dot + quiet text
// rather than outlined pills. Consumers wrap a view in <DetailShell> (which
// owns the counter and the staggered entrance) and compose <DetailHeader> +
// <DetailSection>s inside it.
// ---------------------------------------------------------------------------

const shellVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
}

const blockVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: 'easeOut' } },
}

export function DetailShell({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <motion.div
      variants={shellVariants}
      initial="hidden"
      animate="show"
      className={cn('space-y-9 [counter-reset:detailsec]', className)}
    >
      {children}
    </motion.div>
  )
}

export function DetailHeader({
  title,
  description,
  status,
  logo,
}: {
  title: string
  description?: ReactNode
  status?: ReactNode
  logo?: ReactNode
}) {
  return (
    <motion.header variants={blockVariants} className="space-y-2">
      <div className="flex items-center gap-3">
        {logo && <span className="flex-none">{logo}</span>}
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
        {status}
      </div>
      {description && (
        <p className="max-w-prose text-sm text-muted-foreground">
          {description}
        </p>
      )}
    </motion.header>
  )
}

// Ghost-numbered section. The numeral comes from a pure CSS counter so
// conditional sections renumber themselves; content indents by margin
// (numeral column + gap) so it stays aligned under the title line.
export function DetailSection({
  title,
  aside,
  children,
  className,
  contentClassName,
}: {
  title: string
  /** Optional right-aligned adornment on the header line (count, action…). */
  aside?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
}) {
  return (
    <motion.section variants={blockVariants} className={className}>
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="w-8 flex-none text-right text-[22px] font-extrabold tabular-nums leading-none text-foreground/[0.13] [counter-increment:detailsec] before:content-[counter(detailsec,decimal-leading-zero)]"
        />
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="h-px flex-1 bg-gradient-to-r from-primary/25 to-transparent" />
        {aside && <span className="flex-none">{aside}</span>}
      </div>
      <div className={cn('ml-11 mt-3 space-y-3', contentClassName)}>
        {children}
      </div>
    </motion.section>
  )
}

// Un-numbered animated block — participates in the shell's stagger without
// claiming a ghost numeral (e.g. an Advanced drawer or empty state).
export function DetailBlock({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <motion.div variants={blockVariants} className={className}>
      {children}
    </motion.div>
  )
}

const DOT_TONES = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  error: 'bg-destructive',
  muted: 'bg-muted-foreground/40',
} as const

export function StatusDot({
  tone = 'ok',
  label,
}: {
  tone?: keyof typeof DOT_TONES
  label: string
}) {
  return (
    <span className="flex flex-none items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <span className={cn('h-1.5 w-1.5 rounded-full', DOT_TONES[tone])} />
      {label}
    </span>
  )
}

// Quiet key/value pair for detail metadata (workspace name, linked date…) —
// replaces the bordered two-column grids from the card grammar.
export function DetailFact({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-sm font-medium">{children}</div>
    </div>
  )
}
